/**
 * provider-registry (US-AE-IMP.F6-0118-S03 · @UC9148) — nguồn THUẦN danh sách provider LLM + map
 * provider → adapter-kind. Router runtime (S05) dùng để chọn adapter mà KHÔNG rải string literal khắp
 * nơi (DRY, testable không container).
 *
 * INVARIANTS (HARD):
 *  - Domain THUẦN, no-IO, no-secret: KHÔNG chứa endpoint/khoá/tiêu-đề mạng (đó là adapter S04).
 *  - Một nguồn danh sách provider (đồng bộ db S01 cột provider + contracts S02 llmProviderSchema).
 *  - FAIL-CLOSED (index §6c): `resolveAdapterKind` PHẢI throw với provider chưa có adapter — KHÔNG mặc
 *    định về một adapter tuỳ tiện. modelId tuỳ ý CHỈ chạy khi thuộc một provider đã hỗ trợ; provider
 *    MỚI (ngoài 3) = PHẢI viết adapter mới (không có generic OpenAI-compatible adapter ở task này).
 */

/** Nhà cung cấp LLM có adapter runtime. Khoá cứng 3 (provider mới ⇒ task riêng thêm adapter). */
export type LlmProvider = "anthropic" | "google" | "openai";

/** Loại adapter runtime tương ứng provider (apps/api/.../llm/*). */
export type AdapterKind = "claude" | "gemini" | "openai";

/** Danh sách provider CÓ adapter (một nguồn — đồng bộ contracts LLM_PROVIDERS + db). */
export const SUPPORTED_PROVIDERS: readonly LlmProvider[] = ["anthropic", "google", "openai"];

/** Map provider → adapter-kind (hợp đồng chọn adapter — KHÔNG import adapter thật; giữ thuần no-IO). */
const PROVIDER_ADAPTER_KIND: Readonly<Record<LlmProvider, AdapterKind>> = {
  anthropic: "claude",
  google: "gemini",
  openai: "openai",
};

/** Guard thuần: value có phải provider đã hỗ trợ (dùng fail-closed provider chưa map). */
export function isSupportedProvider(value: string): value is LlmProvider {
  return (SUPPORTED_PROVIDERS as readonly string[]).includes(value);
}

/**
 * Map provider → adapter-kind. THROW rõ ràng nếu provider chưa hỗ trợ (fail-closed — router S05 bắt
 * và về hành vi an toàn). KHÔNG mặc định về adapter tuỳ tiện.
 */
export function resolveAdapterKind(provider: string): AdapterKind {
  if (!isSupportedProvider(provider)) {
    throw new Error(
      `[provider-registry] provider chưa hỗ trợ: "${provider}" (chưa có adapter — fail-closed)`,
    );
  }
  return PROVIDER_ADAPTER_KIND[provider];
}

/**
 * Heuristic suy provider từ modelId prefix (claude-*→anthropic, gemini-*→google, gpt-*→openai). CHỈ
 * dùng làm gợi ý/backfill — provider EXPLICIT ở AiModelConfig là nguồn chuẩn (KHÔNG suy đoán khi đã có
 * cột provider). Trả null nếu không nhận diện được.
 */
export function defaultProviderForModelId(modelId: string): LlmProvider | null {
  const id = modelId.trim().toLowerCase();
  if (id.startsWith("claude")) return "anthropic";
  if (id.startsWith("gemini")) return "google";
  if (id.startsWith("gpt") || id.startsWith("o1") || id.startsWith("o3") || id.startsWith("o4")) {
    return "openai";
  }
  return null;
}

/**
 * 🔴 US-AI-0785 (@UC0149) — HÌNH DẠNG REQUEST THEO HỌ MODEL OpenAI.
 *
 * Họ GPT-5 (và o1/o3/o4) là model *reasoning*: Chat Completions **từ chối** `temperature` khác mặc
 * định (HTTP 400) và đòi `max_completion_tokens` thay `max_tokens`. Bốn việc Goppy giao cho LLM đều là
 * "chọn từ tập đóng + trích slot" — không cần suy luận — nên `reasoning_effort:"minimal"` là mặc định
 * đúng: token reasoning tính vào output VÀ vào trần token; để `medium` là trả tiền + chờ cho thứ
 * không dùng, và có thể ăn hết trần rồi trả rỗng.
 *
 * Đây là BẢNG DỮ LIỆU theo prefix, một nguồn; adapter đọc bảng, không tự `if` theo tên model.
 * Model lạ ⇒ mặc định *sampling* (cố ý): nếu đoán sai, provider trả 400 và lỗi HIỆN RA, thay vì lặng lẽ
 * bỏ tham số mà cấu hình đã khai (cấu hình engine không tôn trọng = nói dối về cấu hình).
 */
export interface OpenAiRequestShape {
  /** true ⇒ gửi `temperature` từ AiModelConfig; false ⇒ bỏ (model chỉ nhận mặc định). */
  readonly samplingSupported: boolean;
  /** Chỉ có ở model reasoning: mức THẤP NHẤT mà họ đó chấp nhận. */
  readonly reasoningEffort?: OpenAiReasoningEffort;
}

export type OpenAiReasoningEffort = "minimal" | "none" | "low";

/**
 * Bảng họ model reasoning → (mức reasoning thấp nhất được nhận, có nhận `temperature` không). ĐO
 * 14/09/2026 trên API thật (một lời gọi mỗi ô), vì mỗi thế hệ đặt tên mức khác nhau và gửi sai là HTTP
 * 400 `unsupported_value`:
 *  · gen 5.0 (`gpt-5`, `gpt-5-mini`, `gpt-5-nano`): `minimal`; `temperature` ≠ 1 ⇒ 400 (`none` KHÔNG có)
 *  · gen 5.x có dấu chấm (`gpt-5.1`, `gpt-5.4-nano`, `gpt-5.6-*`): `none`, và **NHẬN `temperature`**
 *    khi `none` (5.4-nano + none + 0.2 ⇒ 200; + `low` ⇒ 400) — review PR #782 H1 đo, tác giả đo lại.
 *    Bản đầu gán "không sampling" cho cả gen 5.x là suy từ ca `minimal` bị từ chối — siết sai chiều:
 *    contracts từ chối cấu hình hợp lệ, adapter WARN sai.
 *  · o-series (`o1`, `o3`, `o4`): `low` — o4-mini từ chối `minimal`; `temperature` ở o-series CHƯA ĐO
 *    ⇒ giữ "không sampling" (sai chiều này chỉ mất một tham số + WARN, không 400).
 * THỨ TỰ có ý nghĩa: `gpt-5.` phải đứng TRƯỚC `gpt-5` (prefix match lấy hàng đầu tiên khớp).
 * Thêm họ mới = thêm một hàng SAU KHI ĐO, không sửa adapter.
 */
const OPENAI_REASONING_FAMILIES: ReadonlyArray<readonly [prefix: string, shape: OpenAiRequestShape]> =
  Object.freeze([
    ["gpt-5.", Object.freeze({ samplingSupported: true, reasoningEffort: "none" })],
    ["gpt-5", Object.freeze({ samplingSupported: false, reasoningEffort: "minimal" })],
    ["o1", Object.freeze({ samplingSupported: false, reasoningEffort: "low" })],
    ["o3", Object.freeze({ samplingSupported: false, reasoningEffort: "low" })],
    ["o4", Object.freeze({ samplingSupported: false, reasoningEffort: "low" })],
  ]);

const OPENAI_SAMPLING_SHAPE: OpenAiRequestShape = Object.freeze({ samplingSupported: true });

export function openaiRequestShape(modelId: string): OpenAiRequestShape {
  const id = modelId.trim().toLowerCase();
  const family = OPENAI_REASONING_FAMILIES.find(([prefix]) => id.startsWith(prefix));
  return family ? family[1] : OPENAI_SAMPLING_SHAPE;
}

/**
 * 🔴 AI2D-LONG-SUMMARY (@UC0455) — tham số `thinking` của làn ĐỌC trên Anthropic, theo model.
 *
 * `claude-sonnet-5-5` KHÔNG có `thinking` trong request ⇒ **suy nghĩ thích ứng** ở effort mặc định `high`, chữ suy nghĩ ẩn (`display` mặc
 * định `omitted`), và token suy nghĩ **tính vào `max_tokens`**. Đo 17/10 (3 lời gọi thật, câu tóm tắt S0-Q07, trần 1050): không gửi ⇒
 * `max_tokens`, 1050/1050 token là suy nghĩ, 0 chữ, 8,1 s; `between_tools` ⇒ `end_turn`, 0 suy nghĩ, 496 token, 745 ký tự, 4,0 s. Làn đọc trả
 * lời trên dữ liệu đã dựng (không suy luận nhiều bước, không công cụ) — suy nghĩ ngầm chỉ ăn trần + giờ.
 *
 * BẢNG ĐÓNG, chỉ hàng ĐÃ ĐO (khuôn `OPENAI_REASONING_FAMILIES`): `between_tools` chỉ Sonnet 5.5 nhận — model khác ⇒ HTTP 400 ⇒ `config` ⇒ làn
 * đọc lùi 100%. Model chưa đo ⇒ `undefined` ⇒ không gửi gì (giữ hành vi cũ). Tài liệu nêu Sonnet 5 / Haiku 5.5 nhận `{type:"disabled"}`, Opus
 * 5.5 không tắt được (chỉ hạ `output_config.effort`) — CHƯA ĐO, thêm hàng sau khi đo. `between_tools` không kèm trường nào khác và không đi với
 * effort `xhigh`/`max` (⇒ 400) — adapter không gửi effort.
 * Adapter gọi khi router đặt `controlThinking` (làn đọc · lượt LEO THANG phiên dịch — AI-SMOOTH-5); lượt phiên dịch thường / polish không đổi.
 */
export interface AnthropicThinkingParam {
  readonly type: "between_tools";
}

const ANTHROPIC_ANSWER_THINKING: ReadonlyArray<readonly [model: RegExp, thinking: AnthropicThinkingParam]> = Object.freeze([
  [/^claude-sonnet-5-5(?:-\d{8})?$/u, Object.freeze({ type: "between_tools" as const })],
]);

export function anthropicAnswerThinking(modelId: string): AnthropicThinkingParam | undefined {
  const id = modelId.trim().toLowerCase();
  return ANTHROPIC_ANSWER_THINKING.find(([re]) => re.test(id))?.[1];
}
